import { schema } from 'prose-core'
import cx from 'classnames'
import { Decorator } from './Types'
import { Decoration } from 'prosemirror-view'
import { TrackedChangeType } from 'prose-core'
import { Node, ResolvedPos } from 'prosemirror-model'
import { entirelyDeleted, isDeletion, isEmptyParagraph } from './utils'
import { DATA_TYPE_ATTR } from './constants'

import Styles from './TrackChanges.module.scss'
import { absurd } from 'types'

export const deletedNonEmptyParagraphPrecededByEmptyParagraph = (node: Node, $pos: ResolvedPos) => {
  return (
    isDeletion(node) &&
    node.type === schema.nodes.paragraph &&
    node.childCount > 0 &&
    $pos.nodeBefore &&
    $pos.nodeBefore.type === schema.nodes.paragraph &&
    $pos.nodeBefore.childCount === 0
  )
}

const paragraphDecorator: Decorator = (type, node, $pos, uuid, selected) => {
  const nodeBefore = $pos.nodeBefore
  const decos: Decoration[] = []
  const beforeClass = getBeforeClass(type)

  if (nodeBefore && deletedNonEmptyParagraphPrecededByEmptyParagraph(node, $pos)) {
    /**
     * Decorate the previous paragraph as deleted and empty to make it disappear
     * when decorations are turned off.
     */
    decos.push(
      Decoration.node($pos.pos - nodeBefore.nodeSize, $pos.pos, {
        class: cx(Styles.paragraph, beforeClass),
      }),
    )
  }

  if ([TrackedChangeType.Insertion, TrackedChangeType.Deletion].includes(type)) {
    decorateNodeBeforeIfApplicable(type, $pos, decos)

    decos.push(
      Decoration.node($pos.pos, $pos.pos + node.nodeSize, {
        class: cx(Styles.paragraph, Styles[type], {
          [Styles.selected]: selected,
          [Styles.empty]: node.childCount === 0 || entirelyDeleted(node),
          [Styles.nonEmpty]: node.childCount > 0 && !entirelyDeleted(node),
        }),
        'data-uuid': uuid,
        'data-type': DATA_TYPE_ATTR,
      }),
    )
  }

  if (type === TrackedChangeType.NodeTypeChange) {
    decos.push(
      Decoration.node($pos.pos, $pos.pos + node.nodeSize, {
        class: cx(Styles.paragraph, Styles[type], selected && Styles.selected),
        'data-uuid': uuid,
        'data-type': DATA_TYPE_ATTR,
      }),
    )
  }

  return decos
}

/*
TODO: Create a chain function
That takes these Decorator functions and returns a new Decorator function
function chainDecorators(...decorators: Decorator[]): Decorator {
  return (type, node, $pos, uuid, selected) => {
    const decos: Decoration[] = []
    for(const decorator of decorators) {
      const res = decorator(type, node, $pos, uuid, selected);
      // Check if at least one decorator returned a non null
      if (Array.isArray(res)) decos.push(...res)
      else decos.push(res)
    }
    return decos
  }
}
*/
function decorateNodeBeforeIfApplicable(
  type: TrackedChangeType,
  { nodeBefore, pos }: ResolvedPos,
  decos: Decoration[],
) {
  if (nodeBefore?.type.groups.includes('textWrap')) {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const posOfNodeBefore = pos - nodeBefore!.nodeSize
    decos.push(
      Decoration.node(posOfNodeBefore, posOfNodeBefore + nodeBefore.nodeSize, {
        class: cx(
          Styles.paragraph,
          Styles.pilcrow,
          getBeforeClass(type),
          nodeBefore.childCount === 0 || entirelyDeleted(nodeBefore) ? Styles.empty : Styles.nonEmpty,
        ),
      }),
    )
  }
}

// function nodeRepr(node?: Node | null) {
//   if (!node) return node
//   return `${node.type.name}${isDeletion(node) ? '(deleted)' : ''}`
// }

// Verbose version of: Styles[camelCase(`before ${type}`)]
// But a typed version, easier to navigate into with intellisense
function getBeforeClass(type: TrackedChangeType): string {
  switch (type) {
    case TrackedChangeType.Insertion:
      return Styles.beforeInsertion
    case TrackedChangeType.Deletion:
      return Styles.beforeDeletion
    case TrackedChangeType.FormattingChange:
      return Styles.beforeFormattingChange
    case TrackedChangeType.NodeTypeChange:
      return Styles.beforeNodeTypeChange
    default:
      absurd(type)
      return ''
  }
}

const imageDecorator: Decorator = (type, node, $pos, uuid, selected) => {
  return Decoration.node($pos.pos, $pos.pos + node.nodeSize, {
    class: cx(Styles.image, Styles[type], selected && Styles.selected),
    'data-uuid': uuid,
    'data-type': DATA_TYPE_ATTR,
  })
}

const headingDecorator: Decorator = (type, node, $pos, uuid, selected) => {
  return Decoration.node($pos.pos, $pos.pos + node.nodeSize, {
    class: cx(Styles.heading, Styles[type], selected && Styles.selected),
    'data-uuid': uuid,
    'data-type': DATA_TYPE_ATTR,
  })
}

const listItemDecorator: Decorator = (type, node, $pos, uuid, selected) => {
  return Decoration.node($pos.pos, $pos.pos + node.nodeSize, {
    class: cx(Styles.listItem, Styles[type], {
      [Styles.selected]: selected,
      [Styles.empty]: node.content.content.every(isEmptyParagraph) || entirelyDeleted(node),
    }),
    'data-uuid': uuid,
    'data-type': DATA_TYPE_ATTR,
  })
}

const smartLinkDecorator: Decorator = (type, node, $pos, uuid, selected) => {
  return Decoration.node($pos.pos, $pos.pos + node.nodeSize, {
    class: cx(Styles.smartLink, Styles[type], selected && Styles.selected),
    'data-uuid': uuid,
    'data-type': DATA_TYPE_ATTR,
  })
}

const defaultBlockDecorator: Decorator = (type, node, $pos, uuid, selected) => {
  const decos: Decoration[] = []
  if ([TrackedChangeType.Insertion, TrackedChangeType.Deletion].includes(type)) {
    decorateNodeBeforeIfApplicable(type, $pos, decos)
  }
  const decoration = Decoration.node($pos.pos, $pos.pos + node.nodeSize, {
    class: cx(Styles.defaultBlock, Styles[type], selected && Styles.selected, {
      Unstyled: type === TrackedChangeType.Deletion,
    }),
    nodeName: 'div',
    'data-uuid': uuid,
    'data-type': DATA_TYPE_ATTR,
  })
  return decos.concat(decoration)
}

const leafBlockDecorator: Decorator = (type, node, $pos, uuid, selected) => {
  return Decoration.node($pos.pos, $pos.pos + node.nodeSize, {
    class: cx(Styles.leafBlock, Styles[type], selected && Styles.selected),
    nodeName: 'div',
    'data-uuid': uuid,
    'data-type': DATA_TYPE_ATTR,
  })
}

const tableDecorator: Decorator = (type, node, $pos, uuid, selected) => {
  return Decoration.node($pos.pos, $pos.pos + node.nodeSize, {
    class: cx(Styles.table, Styles[type], selected && Styles.selected),
    'data-uuid': uuid,
    'data-type': DATA_TYPE_ATTR,
  })
}

const tableRowDecorator: Decorator = (type, node, $pos, uuid, selected) => {
  return Decoration.node($pos.pos, $pos.pos + node.nodeSize, {
    class: cx(Styles.defaultBlock, Styles[type], selected && Styles.selected),
    'data-uuid': uuid,
    'data-type': DATA_TYPE_ATTR,
  })
}

const tableCellDecorator: Decorator = (type, node, $pos, uuid, selected) => {
  return Decoration.node($pos.pos, $pos.pos + node.nodeSize, {
    class: cx(Styles.defaultBlock, Styles[type], selected && Styles.selected),
    'data-uuid': uuid,
    'data-type': DATA_TYPE_ATTR,
  })
}

const nullDecorator: Decorator = () => {
  return null
}

export const nodesDecorators: { [node in keyof typeof schema.nodes]: Decorator } = {
  ai_suggestion: nullDecorator,
  asana_embed: defaultBlockDecorator,
  box: defaultBlockDecorator,
  br: nullDecorator,
  card: nullDecorator,
  check_list: nullDecorator,
  code_block: defaultBlockDecorator,
  collection: nullDecorator,
  column_group: nullDecorator,
  column: nullDecorator,
  doc: nullDecorator,
  download: defaultBlockDecorator,
  github_embed: defaultBlockDecorator,
  heading: headingDecorator,
  iframe: defaultBlockDecorator,
  image: imageDecorator,
  generate_image: imageDecorator,
  jira_embed: defaultBlockDecorator,
  line_divider: defaultBlockDecorator,
  list_item: listItemDecorator,
  mermaid_embed: defaultBlockDecorator,
  ordered_list: nullDecorator,
  paragraph: paragraphDecorator,
  smartLink: smartLinkDecorator,
  summary: nullDecorator,
  table_cell: tableCellDecorator,
  table_header: tableRowDecorator,
  table_row: tableRowDecorator,
  table: tableDecorator,
  table_of_contents: leafBlockDecorator,
  text: nullDecorator,
  toggle_list: nullDecorator,
  unordered_list: nullDecorator,
  vote_list: nullDecorator,
  vote_list_item: listItemDecorator,
}
